import { Button, CircleCheckBigIcon, Combobox } from '@buffer-mono/popcorn'
import type { ButtonProps } from '@buffer-mono/popcorn'

import { FilterButton } from '~publish/components/FilterButton'
import type { PostStatus } from '~publish/pages/AllChannels/PostList/usePaginatedPostList'

import type { JSX } from 'react'

/**
 * Status options in the order they appear as Board columns, so the filter
 * list and the columns it hides read the same way.
 */
const STATUS_OPTIONS: readonly { value: PostStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'needs_approval', label: 'Needs approval' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'sending', label: 'Sending' },
  { value: 'sent', label: 'Published' },
  { value: 'error', label: 'Failed' },
]

type FilterByStatusProps = Pick<ButtonProps, 'size' | 'variant'> & {
  /** Selected statuses; empty means every status. */
  value: string[]
  onSelect: (statuses: string[]) => void
  className?: string
}

/** Narrows a Combobox selection down to the `PostStatus` values it contains. */
export function toPostStatuses(values: string[]): PostStatus[] {
  const validStatuses = new Set<string>(
    STATUS_OPTIONS.map((option) => option.value),
  )
  return values.filter((value): value is PostStatus => validStatuses.has(value))
}

/**
 * Multi-select filter for a post's status, modelled on
 * `~publish/components/FilterByTag`.
 */
export const FilterByStatus = ({
  size = 'medium',
  variant = 'tertiary',
  value,
  onSelect,
  className,
}: FilterByStatusProps): JSX.Element => {
  return (
    <Combobox multiple value={value} onChange={onSelect}>
      <Combobox.Trigger className={className}>
        <FilterButton
          icon={<CircleCheckBigIcon />}
          count={value.length}
          variant={variant}
          size={size}
        >
          Status
        </FilterButton>
      </Combobox.Trigger>
      <Combobox.Content>
        {/* No search input: six rows, never scrolls. */}
        <Combobox.List>
          {STATUS_OPTIONS.map(({ value: status, label }) => (
            <Combobox.Item key={status} value={status}>
              {label}
            </Combobox.Item>
          ))}
        </Combobox.List>
        <Combobox.Footer>
          <Combobox.Clear>
            <Button variant="tertiary">Clear All</Button>
          </Combobox.Clear>
        </Combobox.Footer>
      </Combobox.Content>
    </Combobox>
  )
}
